import { useQuery } from '@tanstack/react-query';
import { contentApi, queryKeys } from '../api/endpoints.js';
import { admissionSteps } from '../constants/admissions.js';
import { stages as localStages } from '../constants/stages.js';
import { teachers as localTeachers } from '../constants/teachers.js';
import {
  featuredTestimonial as localFeatured,
  testimonials as localTestimonials,
} from '../constants/testimonials.js';

/*
 * The public page reads its copy from the API, with the constants in
 * src/constants/ as the starting value rather than as a loading state.
 *
 * That combination is deliberate:
 *
 *   - the prerendered HTML and the first paint both have real copy, so there is
 *     no spinner and no layout shift, and a crawler that runs no JavaScript
 *     still sees the whole page;
 *   - the database stays the source of truth, so editing a stage description
 *     shows up without a redeploy;
 *   - if the API is down, the site renders exactly as it does today instead of
 *     showing an error. A marketing page has no business failing because a
 *     database is briefly unreachable.
 *
 * Presentation-only fields — the imported photo assets, the eyebrow colour —
 * are not in the database and are merged back in from the local row.
 */

function useContent(queryKey, queryFn, merge, fallback) {
  const { data, isError } = useQuery({
    queryKey,
    queryFn,
    // A stale-but-rendered page beats an empty one; never blank the content.
    placeholderData: (previous) => previous,
  });

  if (isError || !data) return fallback;

  try {
    return merge(data);
  } catch (error) {
    // Malformed payload — a schema change mid-deploy, say. Show the copy we
    // shipped with rather than crashing the homepage over it.
    console.error('Could not merge API content, using bundled copy:', error);
    return fallback;
  }
}

export function useStages() {
  return useContent(
    queryKeys.stages,
    contentApi.stages,
    (rows) =>
      rows.map((row) => {
        const local = localStages.find((stage) => stage.id === row.slug);

        return {
          id: row.slug,
          eyebrow: row.eyebrow,
          ages: row.ages,
          title: row.title,
          body: row.body,
          tags: row.tags ?? [],
          linkLabel: row.linkLabel,
          side: row.side,
          photoAlt: row.photoAlt ?? local?.photoAlt ?? '',
          // Bundled asset unless an uploaded URL exists for it.
          photo: row.photoUrl ?? local?.photo,
          eyebrowClass: local?.eyebrowClass ?? 'text-coral',
        };
      }),
    localStages,
  );
}

export function useTeachers() {
  return useContent(
    queryKeys.teachers,
    contentApi.teachers,
    (rows) =>
      rows.map((row) => ({
        name: row.name,
        role: row.role,
        quote: row.quote,
        photo: row.photoUrl ?? undefined,
      })),
    localTeachers,
  );
}

export function useTestimonials() {
  return useContent(
    queryKeys.testimonials,
    contentApi.homepageTestimonials,
    (data) => ({
      featured: data.featured
        ? {
            // The eyebrow is a fixed piece of section chrome, not editable copy.
            eyebrow: localFeatured.eyebrow,
            quote: data.featured.quote,
            name: data.featured.attribution,
            detail: data.featured.detail ?? '',
          }
        : localFeatured,
      others: (data.others ?? []).map((row) => ({
        quote: row.quote,
        attribution: row.attribution,
        tone: row.tone,
      })),
    }),
    { featured: localFeatured, others: localTestimonials },
  );
}

/** Not in the database — the four admission steps are part of the page design. */
export function useAdmissionSteps() {
  return admissionSteps;
}
